import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import useUploadFiles from 'api/hooks/useUploadFiles';
import type { UploadHubListItem } from 'api/services/uploadFileService';
import SharedDocumentPreview from 'components/ShareDocument/SharedDocumentPreview';
import UploadHubModalAdd, { UploadHubModalMode } from 'components/UploadHub/UploadHubModalAdd';
import UploadHubDeleteModal from 'components/UploadHub/UploadHubDeleteModal';
import UploadHubPasscodeModal from 'components/UploadHub/UploadHubPasscodeModal';
import Loading from 'components/Login/Loading';
import { buildDocumentSharePageV2Url } from 'utils/buildShareDocumentV2Link';
import { copyTextToClipboard } from 'components/Dashboard/helpers/copyFunctionality';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import AddToEmailCampaignButton from './AddToEmailCampaignButton';
import AddToLobbyingMaterialButton from './AddToLobbyingMaterialButton';

const UploadHubPreviewPage = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const navigate = useNavigate();
  const { getUploadHubList } = useUploadFiles();
  const { addMetaTags } = useAddMetaTags();

  const [file, setFile] = useState<UploadHubListItem | null>(null);
  const [isLoading, setIsLoading] = useState(!file);
  const [editModalMode, setEditModalMode] = useState<Extract<UploadHubModalMode, 'editFile' | 'editDesign'> | null>(
    null
  );
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [passcodeEnabled, setPasscodeEnabled] = useState(false);
  const [isPasscodeModalOpen, setIsPasscodeModalOpen] = useState(false);

  const fetchFile = useRef<((signal: AbortSignal) => Promise<void>) | null>(null);
  fetchFile.current = async (signal) => {
    try {
      const results = await getUploadHubList({ signal });
      const file = results?.find((item: UploadHubListItem) => item.publicId === publicId) ?? null;
      setFile(file);
      const title = `Your files - ${file?.fileName} - PolicyMogul`;
      addMetaTags({ title, hash: location.hash });
      setIsLoading(false);
    } catch {
      // apiClient's error interceptor already shows a toast
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchFile.current?.(controller.signal);
    return () => controller.abort('Upload hub preview cancelled.');
  }, []);

  const [heightContainer, containerRef] = useHeightContainer();

  return (
    <div className='upload-hub-preview-page'>
      <div className='bg-white monitor-items-bar'>
        <div className='mx-auto main-content-wrapper flex-centered justify-content-between resize-elements-container'>
          <div className='side-left-element pl-md-5 pl-3 d-none d-lg-flex flex-centered'>
            <Link
              className='general-button action-button rounded-pill px-4 paragraph-p2 d-inline-block nowrap-item'
              to={'/files'}
            >
              <span className='icon-long-arrow-left mr-2 paragraph-p4' /> Back to files
            </Link>
          </div>
          <div className='main-content flex-centered position-relative filters-general-container justify-content-between pr-xl-5 px-md-4 px-3'>
            {isLoading ? (
              <div>
                <SkeletonTheme baseColor='#f2f2f2'>
                  <Skeleton height={36} width={100} borderRadius={18} className='mr-3' />
                  <Skeleton height={36} width={90} borderRadius={18} className='mr-3' />
                  <Skeleton height={36} width={36} circle className='mr-2' />
                  <Skeleton height={36} width={36} circle className='ml-1' />
                </SkeletonTheme>
              </div>
            ) : (
              file && (
                <>
                  <p className='mb-0 title-h4-bold nowrap-item text-truncate'>Preview: {file?.fileName}</p>
                  <div className='flex-centered'>
                    <button
                      className='general-button rounded-pill border pr-3 specific-left-padding-10 specific-y-padding-3 mr-3 hovered-button bg-main-white'
                      onClick={() => {
                        setEditModalMode('editFile');
                        setOpenEditModal(true);
                      }}
                    >
                      <span className='icon-three-dots paragraph-p3 mr-2 main-light-text d-inline-block-centered' />
                      File details
                    </button>
                    <button
                      className='general-button rounded-pill border pr-3 specific-left-padding-10 specific-y-padding-3 rounded-pill mr-3 hovered-button report-design-button bg-main-white upload-design-button'
                      onClick={() => {
                        setEditModalMode('editDesign');
                        setOpenEditModal(true);
                      }}
                    >
                      Design
                    </button>
                    <button
                      className='general-button border specific-y-padding-7 specific-x-padding-22 mr-3 rounded-button d-flex nowrap-item bg-main-white'
                      onClick={() => {
                        const next = !passcodeEnabled;
                        setPasscodeEnabled(next);
                        if (next) setIsPasscodeModalOpen(true);
                      }}
                    >
                      <span>Passcode </span>
                      <span
                        className='icon-info specific-top-margin-6 specific-left-margin-8 paragraph-p3'
                        data-tooltip-id='general-tooltip'
                        data-tooltip-content='Require a passcode to open this file'
                        data-tooltip-place='bottom'
                      />
                      <span
                        className={`specific-left-margin-18 toggle-button-${passcodeEnabled ? 'active' : 'inactive'}`}
                      >
                        <span className='toggle-button' />
                      </span>
                    </button>
                    <button
                      className=' general-button rounded-pill border pr-3 specific-left-padding-13 specific-y-padding-3 rounded-pill specific-right-margin-1 hovered-button  bg-main-white'
                      onClick={() => {
                        copyTextToClipboard(buildDocumentSharePageV2Url(file.publicId), 'Link to file copied');
                      }}
                    >
                      <span className='icon-copy mr-2 d-inline-block-centered paragraph-p3' />
                      Copy link to file
                    </button>
                    <MoreActionsButton file={file} setIsDeleteOpen={setIsDeleteOpen} />
                  </div>
                </>
              )
            )}
          </div>
        </div>
      </div>

      <div className='upload-hub-preview-content' ref={containerRef}>
        <CustomScrollbar
          className='simple-scrollbar'
          style={{ height: `${heightContainer}px` }}
          maximalThumbYSize={100}
        >
          {isLoading ? (
            <Loading />
          ) : !file ? (
            <div className='row mx-auto main-content-wrapper align-items-start flex-grow-1 pt-5'>
              <div className='w-100 px-xl-5 px-md-4 px-3 h-100 mx-auto'>
                <p className='text-center mt-5 pt-5 title-h4 main-light-text px-5 mt-5 pt-lg-5'>File not found</p>
              </div>
            </div>
          ) : (
            <SharedDocumentPreview
              document={{
                documentUrl: file.url,
                fileName: file.fileName,
                contentType: file.contentType,
                backgroundColor: file.backgroundColor,
                organisationLogo: file.organisationLogoUrl,
              }}
            />
          )}
        </CustomScrollbar>
      </div>

      {file && (
        <>
          <UploadHubModalAdd
            mode={editModalMode ?? 'editFile'}
            editingFile={file}
            openState={{
              isOpen: openEditModal,
              setIsOpen: setOpenEditModal,
            }}
            onUpdated={setFile}
          />
          <UploadHubDeleteModal
            openState={{ isOpen: isDeleteOpen, setIsOpen: setIsDeleteOpen }}
            itemIdState={{ itemId: file.id, setItemId: () => {} }}
            onDeleted={() => navigate('/files')}
          />
          <UploadHubPasscodeModal
            openState={{ isOpen: isPasscodeModalOpen, setIsOpen: setIsPasscodeModalOpen }}
            onClose={(hasPasscode) => {
              if (!hasPasscode) setPasscodeEnabled(false);
            }}
          />
        </>
      )}
    </div>
  );
};

interface MoreActionsButtonProps {
  setIsDeleteOpen: (value: boolean) => void;
  file: UploadHubListItem | null;
}

const MoreActionsButton = (props: MoreActionsButtonProps) => {
  const { file, setIsDeleteOpen } = props;
  const [isMoreActionsOpen, setIsMoreActionsOpen] = useState<boolean>(false);
  const [position, setPosition] = useState({ top: '0px', left: '0px', opacity: 0 });
  const moreActionsRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useOutsideElement(moreActionsRef, () => setIsMoreActionsOpen(false));
  useLayoutEffect(() => {
    if (!isMoreActionsOpen) return;
    const { left, bottom, width } = buttonRef.current?.getBoundingClientRect() ?? ({} as DOMRect);
    const { width: boxWidth } = boxRef.current?.getBoundingClientRect() ?? ({} as DOMRect);
    setPosition({ top: `${bottom + 8}px`, left: `${left + width - boxWidth}px`, opacity: 1 });
  }, [isMoreActionsOpen]);

  return (
    <>
      <div className='ml-1' ref={moreActionsRef}>
        <button
          ref={buttonRef}
          className={`general-button circle-button circle-button-36 hovered-button flex-centered justify-content-center`}
          onClick={() => setIsMoreActionsOpen(!isMoreActionsOpen)}
        >
          <span className='icon-three-dots paragraph-p1' />
        </button>
        {isMoreActionsOpen && file && (
          <div
            ref={boxRef}
            className='position-fixed box-shadow rounded py-2 bg-main-white width-fit-content z-max'
            style={{ top: position.top, left: position.left, opacity: position.opacity }}
          >
            <AddToEmailCampaignButton setIsMoreActionsOpen={setIsMoreActionsOpen} file={file} />
            <AddToLobbyingMaterialButton setIsMoreActionsOpen={setIsMoreActionsOpen} file={file} />
            <button
              className='w-100 d-flex align-items-center text-left general-button list-action-button specific-right-padding-28 pl-3 border-top mt-2 py-2 text-main-danger'
              onClick={() => {
                setIsMoreActionsOpen(false);
                setIsDeleteOpen(true);
              }}
            >
              <span
                className='political-updates-nav-icon specific-left-margin-1 icon-delete'
                style={{ marginRight: '-1px' }}
              />
              Delete file
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default UploadHubPreviewPage;
